import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { CheckoutDialog } from "@/components/CheckoutDialog";
import { SERVICES, formatTZS } from "@/data/services";
import { Button } from "@/components/ui/button";
import { Check, ArrowLeft, ArrowRight } from "lucide-react";

export default function ServiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const service = SERVICES.find((s) => s.id === id);
  const [open, setOpen] = useState(false);

  if (!service) {
    return (
      <div className="bg-white min-h-screen flex items-center justify-center text-brand-dark">
        <div className="text-center">
          <p className="mb-4">Service not found.</p>
          <Button onClick={() => navigate("/")} className="bg-brand-blue text-white rounded-sm">Back home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-brand-grey min-h-screen">
      <SEO
        title={`${service.name} Services | Tanz-Bridge International LTD`}
        description={service.description}
        path={`/services/${service.id}`}
        image={service.image}
        schema={{
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: service.name,
          name: `${service.name} Services`,
          description: service.description,
          provider: {
            "@type": "Organization",
            name: "Tanz-Bridge International LTD",
            url: "https://tanzbridge.com",
            logo: "https://tanzbridge.com/assets/tanz-logo.png",
          },
          areaServed: { "@type": "Country", name: "Tanzania" },
          url: `https://tanzbridge.com/services/${service.id}`,
          offers: {
            "@type": "Offer",
            price: service.price,
            priceCurrency: "TZS",
            availability: "https://schema.org/InStock",
          },
        }}
      />
      <Navbar onOrder={() => setOpen(true)} />
      <section className="relative pt-32 pb-16 overflow-hidden grain bg-brand-dark">
        <div className="absolute inset-0 z-0">
          <img src={service.image} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-t from-brand-dark to-brand-dark/60" />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8">
          <button data-testid="detail-back-btn" onClick={() => navigate("/")}
            className="flex items-center gap-2 text-gray-300 hover:text-white mb-8 transition-colors">
            <ArrowLeft size={16} /> Back to services
          </button>
          <h1 className="text-4xl sm:text-6xl font-heading font-extrabold tracking-tighter text-white">{service.name}</h1>
          <p className="text-lg text-gray-300 mt-4 max-w-2xl">{service.tagline}</p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-16 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2">
          <h2 className="text-2xl font-heading mb-4 text-brand-dark">Overview</h2>
          <p className="text-gray-600 leading-relaxed mb-10">{service.description}</p>
          <h3 className="text-xl font-heading mb-5 text-brand-dark">What's included</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            {service.features.map((f) => (
              <div key={f} className="flex items-start gap-3 rounded-sm bg-brand-card border border-gray-200 shadow-sm p-4">
                <Check size={18} className="text-brand-blue mt-0.5 shrink-0" />
                <span className="text-brand-dark text-sm">{f}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="sticky top-28 rounded-lg bg-brand-card border border-gray-200 shadow-sm p-6">
            <p className="text-xs text-gray-400">Starting from</p>
            <p className="text-3xl font-heading text-brand-blue mt-1">{formatTZS(service.price)}</p>
            <p className="text-sm text-gray-500 mt-4 mb-6">One-time engagement fee. Pay securely with M-Pesa.</p>
            <Button data-testid="detail-order-btn" onClick={() => setOpen(true)}
              className="w-full bg-brand-yellow hover:bg-brand-yellowhover text-brand-dark font-semibold rounded-sm py-6 text-base">
              Order this service <ArrowRight size={18} className="ml-2" />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
      <CheckoutDialog open={open} onOpenChange={setOpen} type="order" item={service} />
    </div>
  );
}
